import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { DEFAULT_ARTICLE_TYPE_OPTIONS } from '../../utils/cargoParcelUtils.js';
import companySettingsService from '../../services/companySettingsService.js';

/**
 * Searchable article / package type picker.
 * Enter selects the highlighted match and optionally moves to the next field.
 */
const ArticleTypeSelect = ({
  value = '',
  onChange,
  readOnly = false,
  className = '',
  placeholder = 'Select type',
  options: optionsProp,
  onEnterNext,
  persistNew = true,
}) => {
  const [extraOptions, setExtraOptions] = useState([]);
  const [apiOptions, setApiOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const current = (value || '').toString().trim().toUpperCase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await companySettingsService.listParcelTypes();
        const rows = res?.data || [];
        if (!cancelled) {
          setApiOptions(rows.map((row) => String(row.name || '').toUpperCase()).filter(Boolean));
        }
      } catch {
        if (!cancelled) setApiOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const merged = [
      ...(optionsProp || []),
      ...apiOptions,
      ...DEFAULT_ARTICLE_TYPE_OPTIONS,
      ...extraOptions,
      ...(current ? [current] : []),
    ];
    return [...new Set(merged.filter(Boolean).map((n) => String(n).toUpperCase()))];
  }, [optionsProp, apiOptions, extraOptions, current]);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return options;
    return options.filter((opt) => opt.includes(q));
  }, [options, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectValue = async (next) => {
    const name = String(next || '').trim().toUpperCase();
    if (!name) return;
    onChange(name);
    setQuery('');
    setOpen(false);
    window.setTimeout(() => onEnterNext?.(), 0);
    if (persistNew && !DEFAULT_ARTICLE_TYPE_OPTIONS.includes(name) && !apiOptions.includes(name)) {
      try {
        await companySettingsService.createParcelType({ name });
        setApiOptions((prev) => (prev.includes(name) ? prev : [...prev, name]));
      } catch {
        setExtraOptions((prev) => (prev.includes(name) ? prev : [...prev, name]));
      }
    }
  };

  if (readOnly) {
    return (
      <div className={className}>
        {current ? current.charAt(0) + current.slice(1).toLowerCase() : '—'}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : (current || '')}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => {
          setQuery(e.target.value.toUpperCase());
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            const pick = (filtered.length
              ? filtered[Math.min(Math.max(highlight, 0), filtered.length - 1)]
              : query.trim().toUpperCase()) || current;
            if (pick) selectValue(pick);
          } else if (e.key === 'Tab' && open && filtered.length) {
            e.preventDefault();
            selectValue(filtered[Math.min(Math.max(highlight, 0), filtered.length - 1)]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="w-full min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 uppercase text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.length === 0 && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
              onClick={() => selectValue(query)}
            >
              + Add “{query.trim().toUpperCase()}”
            </button>
          )}
          {filtered.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => selectValue(opt)}
              className={`w-full text-left px-3 py-1.5 text-sm ${
                idx === highlight ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.charAt(0) + opt.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      )}
      {open && query && (
        <button
          type="button"
          className="absolute right-1 top-1.5 p-1 text-gray-400"
          onClick={() => { setQuery(''); inputRef.current?.focus(); }}
          title="Clear"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
};

export default ArticleTypeSelect;
